import { useEffect, useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * Super Admin → Configuración / Infraestructura → Dominios Web
 * Cloudflare for SaaS vault — NOT R2 S3 credentials.
 */
export default function WebDomainsInfra() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    zone_id: '',
    account_id: '',
    fallback_origin: '',
    cname_target: '',
    enabled: false,
    api_token: '',
  });
  const [hostnames, setHostnames] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [infra, hosts] = await Promise.all([
        api.get('/admin/infrastructure/web-domains'),
        api.get('/admin/infrastructure/web-domains/hostnames').catch(() => ({ data: [] })),
      ]);
      setData(infra.data);
      const cfg = infra.data?.integration?.config || {};
      setForm((f) => ({
        ...f,
        zone_id: cfg.zone_id || '',
        account_id: cfg.account_id || '',
        fallback_origin: cfg.fallback_origin || '',
        cname_target: cfg.cname_target || '',
        enabled: !!cfg.enabled,
        api_token: '',
      }));
      setHostnames(hosts.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/infrastructure/web-domains', {
        zone_id: form.zone_id,
        account_id: form.account_id,
        fallback_origin: form.fallback_origin,
        cname_target: form.cname_target,
        enabled: form.enabled,
        api_token: form.api_token || undefined,
      });
      toast.success('Configuración de Dominios Web guardada');
      setForm((f) => ({ ...f, api_token: '' }));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    try {
      const { data: r } = await api.post('/admin/infrastructure/web-domains/test');
      toast[r.ok ? 'success' : 'error'](r.ok ? 'Conexión OK' : `Fallo: ${r.reason || r.http_status}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const reconcile = async () => {
    try {
      const { data: r } = await api.post('/admin/infrastructure/web-domains/reconcile');
      toast.success(`Reconciliación: ${r.checked} revisados, ${r.active} activos`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const masked = data?.integration?.credentials_masked?.api_token || '—';

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="admin-web-domains-infra">
      <div>
        <h1 className="text-2xl font-bold text-[#0F172A]">Dominios Web</h1>
        <p className="text-sm text-[#64748B] mt-1">
          Configura Cloudflare for SaaS para dominios de clientes.
          <strong> No uses aquí las llaves de R2 (almacenamiento de archivos).</strong>
        </p>
      </div>

      {data?.note && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{data.note}</div>
      )}

      <div className="space-y-3 border rounded-lg p-4 bg-white">
        <label className="block text-sm font-medium">Zone ID</label>
        <Input value={form.zone_id} onChange={(e) => setForm({ ...form, zone_id: e.target.value })} />
        <label className="block text-sm font-medium">Account ID (opcional)</label>
        <Input value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })} />
        <label className="block text-sm font-medium">Origen de respaldo</label>
        <Input value={form.fallback_origin} onChange={(e) => setForm({ ...form, fallback_origin: e.target.value })} placeholder="https://…" />
        <label className="block text-sm font-medium">Destino CNAME (para clientes)</label>
        <Input value={form.cname_target} onChange={(e) => setForm({ ...form, cname_target: e.target.value })} placeholder="sites.ejemplo.com" />
        <label className="block text-sm font-medium">API Token (mínimo alcance) — {masked !== '—' ? `guardado: ${masked}` : 'no configurado'}</label>
        <Input type="password" value={form.api_token} onChange={(e) => setForm({ ...form, api_token: e.target.value })} placeholder="Dejar vacío para conservar" autoComplete="new-password" />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
          Plataforma de dominios activa
        </label>
        <div className="flex flex-wrap gap-2 pt-2">
          <Button onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</Button>
          <Button variant="outline" onClick={test}>Probar conexión</Button>
          <Button variant="outline" onClick={reconcile}>Reconciliar dominios</Button>
        </div>
      </div>

      <div>
        <h2 className="font-semibold mb-2">Custom hostnames ({hostnames.length})</h2>
        <ul className="text-sm space-y-1 max-h-64 overflow-auto border rounded-lg p-3 bg-white">
          {hostnames.map((h) => (
            <li key={h.id} className="flex justify-between gap-2 border-b border-slate-100 py-1">
              <span>{h.hostname}</span>
              <span className="text-[#64748B]">{h.status} · SSL {h.ssl_status || '—'}</span>
            </li>
          ))}
          {!hostnames.length && <li className="text-[#64748B]">Ninguno aún.</li>}
        </ul>
      </div>
    </div>
  );
}
